import { Controller } from "@hotwired/stimulus"
import { apiGet } from "../shared/api"
import { escapeHtml, loadTranslations, t } from "../shared/utils"

export default class extends Controller {
  static values = { slug: String }

  connect() {
    loadTranslations()
    this._load()
  }

  async _load() {
    try {
      const json = await apiGet(`/api/v1/clubs/by_slug/${this.slugValue}`)
      this.club = json.data
      this._render()
    } catch (error) {
      console.error("Error loading club:", error)
      this.element.innerHTML = `<div class="alert alert-danger">${escapeHtml(error.message)}</div>`
    }
  }

  _render() {
    const c = this.club
    if (c.contract_active) {
      this._renderFull(c)
    } else {
      this._renderLimited(c)
    }
  }

  _renderFull(c) {
    const logoHtml = c.logo && c.logo.startsWith("http")
      ? `<img src="${escapeHtml(c.logo)}" alt="${escapeHtml(c.name)}" class="club-show__logo-img">`
      : `<div class="club-show__logo-placeholder"><i class="fa-solid fa-building fa-3x text-muted"></i></div>`

    const fedsHtml = (c.federations || []).map(f => `<span class="badge bg-primary me-1">${escapeHtml(f)}</span>`).join("")
    const descHtml = c.description ? `<div class="club-show__section mb-4"><h5 class="club-show__section-title">${escapeHtml(t("show.about"))}</h5><p>${escapeHtml(c.description)}</p></div>` : ""
    const statsHtml = this._statsHtml(c)
    const teamHtml = this._teamHtml(c)
    const fedMembershipsHtml = this._fedMembershipsHtml(c)
    const contactHtml = this._contactHtml(c)
    const socialHtml = this._socialHtml(c)

    this.element.innerHTML = `<div class="row g-4">
      <div class="col-lg-8 col-12"><div class="club-show__main">
        <div class="club-show__header d-flex align-items-center gap-4 mb-4">
          <div class="club-show__logo">${logoHtml}</div>
          <div><h3 class="mb-1">${escapeHtml(c.name)}</h3>${fedsHtml ? `<div class="club-show__federations">${fedsHtml}</div>` : ""}</div>
        </div>
        ${descHtml}${statsHtml}${teamHtml}${fedMembershipsHtml}
      </div></div>
      <div class="col-lg-4 col-12">
        ${contactHtml}${socialHtml}
        <div class="mt-3"><a href="/clubs/search" class="default-btn w-100"><span><i class="fa-solid fa-arrow-left me-2"></i>${escapeHtml(t("show.back_to_search"))}</span></a></div>
      </div>
    </div>`
  }

  _renderLimited(c) {
    this.element.innerHTML = `<div class="row justify-content-center"><div class="col-lg-8 col-12">
      <div class="club-show__no-subscription text-center py-5">
        <div class="mb-4"><div class="club-show__logo-placeholder d-inline-block"><i class="fa-solid fa-building fa-3x text-muted"></i></div></div>
        <h3 class="mb-3">${escapeHtml(c.name)}</h3>
        ${c.city ? `<p class="text-muted mb-4"><i class="fa-solid fa-location-dot me-1"></i>${escapeHtml(c.city)}</p>` : ""}
        <div class="club-show__no-subscription-message mx-auto" style="max-width: 540px">
          <div class="alert alert-light border shadow-sm p-4">
            <p class="mb-3"><i class="fa-solid fa-info-circle me-2 text-primary"></i>${escapeHtml(t("show.no_subscription.info", { club_name: c.name }))}</p>
            <p class="mb-0 text-muted">${escapeHtml(t("show.no_subscription.encourage"))}</p>
          </div>
        </div>
        <div class="mt-4"><a href="/clubs/search" class="default-btn"><span><i class="fa-solid fa-arrow-left me-2"></i>${escapeHtml(t("show.back_to_search"))}</span></a></div>
      </div>
    </div></div>`
  }

  _statsHtml(c) {
    return `<div class="row g-3 mb-4">
      <div class="col-sm-4"><div class="club-show__stat"><div class="club-show__stat-value">${c.members_count || 0}</div><div class="club-show__stat-label">${escapeHtml(t("show.members"))}</div></div></div>
      <div class="col-sm-4"><div class="club-show__stat"><div class="club-show__stat-value">${c.advanced_dancers_count || 0}</div><div class="club-show__stat-label">${escapeHtml(t("show.advanced_dancers"))}</div></div></div>
      <div class="col-sm-4"><div class="club-show__stat"><div class="club-show__stat-value">${(c.coaches || []).length}</div><div class="club-show__stat-label">${escapeHtml(t("show.coaches"))}</div></div></div>
    </div>`
  }

  _teamHtml(c) {
    if ((!c.owners || c.owners.length === 0) && (!c.coaches || c.coaches.length === 0)) return ""
    let html = `<div class="club-show__section mb-4"><h5 class="club-show__section-title">${escapeHtml(t("show.team"))}</h5>`
    if (c.owners?.length) html += `<div class="mb-2"><span class="text-muted me-2">${escapeHtml(t("show.owners"))}:</span>${c.owners.map(o => escapeHtml(o.name)).join(", ")}</div>`
    if (c.coaches?.length) html += `<div class="mb-2"><span class="text-muted me-2">${escapeHtml(t("show.coaches"))}:</span>${c.coaches.map(o => escapeHtml(o.name)).join(", ")}</div>`
    html += "</div>"
    return html
  }

  _fedMembershipsHtml(c) {
    if (!c.federation_memberships || c.federation_memberships.length === 0) return ""
    let html = `<div class="club-show__section mb-4"><h5 class="club-show__section-title">${escapeHtml(t("show.federations"))}</h5>`
    c.federation_memberships.forEach(fm => {
      html += `<div class="d-flex align-items-center gap-2 mb-2"><span class="badge bg-primary">${escapeHtml(fm.federation_shortcut)}</span>`
      if (fm.region) html += `<span class="text-muted">${escapeHtml(t("show.region"))}: ${escapeHtml(fm.region)}</span>`
      html += "</div>"
    })
    html += "</div>"
    return html
  }

  _contactHtml(c) {
    let items = ""
    const addr = [c.address, c.zipcode, c.city, c.country].filter(Boolean).join(", ")
    if (addr) items += `<dt><i class="fa-solid fa-location-dot me-2"></i>${escapeHtml(t("show.address"))}</dt><dd>${escapeHtml(addr)}</dd>`
    if (c.phone) items += `<dt><i class="fa-solid fa-phone me-2"></i>${escapeHtml(t("show.phone"))}</dt><dd><a href="tel:${escapeHtml(c.phone)}">${escapeHtml(c.phone)}</a></dd>`
    if (c.email) items += `<dt><i class="fa-solid fa-envelope me-2"></i>${escapeHtml(t("show.email"))}</dt><dd><a href="mailto:${escapeHtml(c.email)}">${escapeHtml(c.email)}</a></dd>`
    if (c.website) items += `<dt><i class="fa-solid fa-globe me-2"></i>${escapeHtml(t("show.website"))}</dt><dd><a href="${escapeHtml(c.website)}" target="_blank" rel="noopener">${escapeHtml(c.website.replace(/^https?:\/\//, ""))}</a></dd>`

    return `<div class="club-show__sidebar-card mb-4">
      <h5 class="club-show__sidebar-title"><i class="fa-solid fa-address-card me-2"></i>${escapeHtml(t("show.contact"))}</h5>
      <dl class="club-show__contact">${items}</dl>
    </div>`
  }

  _socialHtml(c) {
    const links = []
    if (c.facebook) links.push(`<a href="${escapeHtml(c.facebook)}" class="club-show__social-link" target="_blank" rel="noopener"><i class="fa-brands fa-facebook fa-lg"></i><span>Facebook</span></a>`)
    if (c.instagram) links.push(`<a href="${escapeHtml(c.instagram)}" class="club-show__social-link" target="_blank" rel="noopener"><i class="fa-brands fa-instagram fa-lg"></i><span>Instagram</span></a>`)
    if (c.youtube) links.push(`<a href="${escapeHtml(c.youtube)}" class="club-show__social-link" target="_blank" rel="noopener"><i class="fa-brands fa-youtube fa-lg"></i><span>YouTube</span></a>`)
    if (links.length === 0) return ""
    return `<div class="club-show__sidebar-card mb-4">
      <h5 class="club-show__sidebar-title"><i class="fa-solid fa-share-nodes me-2"></i>${escapeHtml(t("show.social"))}</h5>
      <div class="club-show__social">${links.join("")}</div>
    </div>`
  }
}
